import { useEffect, useRef } from "react";
import { getEnv } from "@/config/env";
import { getJson } from "@/api/http";

/**
 * The backend caches a live session for 30 seconds per sid, so asking more often
 * than that cannot learn anything new.
 */
const MIN_CHECK_INTERVAL_MS = 30_000;

interface UseSsoSessionWatchOptions {
  /** Only a signed-in member has a session to check. */
  enabled: boolean;
  /** Clock seam for tests. */
  now?: () => number;
}

/**
 * Asks the backend whether the SSO session behind the member cookie is still
 * alive, on mount and whenever the tab regains focus — the two moments where
 * arriving from another service would otherwise show a stale sign-in.
 *
 * Nothing on a timer: a check is only worth making when someone is looking at
 * the page. The reaction to an ended session lives in the shared fetch layer,
 * so there is deliberately nothing to read from the result here — this call's
 * own 401 carries the `session_ended` code and `getJson` turns it into the
 * redirect.
 *
 * Mounted once, at the app root.
 */
export function useSsoSessionWatch({
  enabled,
  now = Date.now,
}: UseSsoSessionWatchOptions): void {
  const lastCheckedAt = useRef<number>(Number.NEGATIVE_INFINITY);

  useEffect(() => {
    if (!enabled) return;

    const check = () => {
      const timestamp = now();
      if (timestamp - lastCheckedAt.current < MIN_CHECK_INTERVAL_MS) return;
      lastCheckedAt.current = timestamp;

      const { apiBaseUrl, siteId } = getEnv();
      void getJson(`${apiBaseUrl}/auth/${siteId}/session`, { retries: 0 });
    };

    check();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") check();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [enabled, now]);
}
